<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { nomeCurto } from 'dashboard/helper/avatar';
import { sugestoesDaTela } from './sugestoesDaTela';
import Icon from '../icon/Icon.vue';

defineProps({
  hasAssistants: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['useSuggestion']);
const { t } = useI18n();
const route = useRoute();

// "Como está o meu dia?" abre a lista em toda tela: o dia da pessoa é a
// pergunta que vale em qualquer lugar (Fase 1 do Lumos, 08/10/2026).
const MEU_DIA = {
  label: 'CAPTAIN.COPILOT.PROMPTS.MEU_DIA.LABEL',
  prompt: 'CAPTAIN.COPILOT.PROMPTS.MEU_DIA.CONTENT',
  icone: 'i-ph-sun-horizon',
};

const promptOptions = computed(() => [MEU_DIA, ...sugestoesDaTela(route)]);

// A saudação: pela hora e pelo nome curto de quem abre ("Boa tarde, Dra.
// Jullya"). O painel fala com a pessoa, como o cartão de entrar.
const usuario = useMapGetter('getCurrentUser');
const saudacao = computed(() => {
  const hora = new Date().getHours();
  let chave = 'BOA_NOITE';
  if (hora >= 5 && hora < 12) chave = 'BOM_DIA';
  else if (hora >= 12 && hora < 18) chave = 'BOA_TARDE';
  const nome = nomeCurto(usuario.value?.name || '');
  const frase = t(`CAPTAIN.COPILOT.SAUDACAO.${chave}`, { nome });
  // Sem nome, sem a vírgula pendurada no fim.
  return nome ? frase : frase.replace(/,\s*$/, '');
});

const handleSuggestion = opt => {
  emit('useSuggestion', t(opt.prompt));
};
</script>

<template>
  <div class="lms flex-1 flex flex-col gap-6 px-1">
    <div class="lms__ola">
      <p class="lms__saudacao">{{ saudacao }}</p>
      <h3 class="lms__titulo">{{ $t('CAPTAIN.COPILOT.PANEL_TITLE') }}</h3>
      <p class="lms__dica">{{ $t('CAPTAIN.COPILOT.KICK_OFF_MESSAGE') }}</p>
    </div>
    <div v-if="!hasAssistants" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('CAPTAIN.ASSISTANTS.NO_ASSISTANTS_AVAILABLE') }}
      </p>
      <router-link
        :to="{
          name: 'captain_assistants_create_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('CAPTAIN.ASSISTANTS.ADD_NEW') }}
      </router-link>
    </div>
    <div v-else class="w-full">
      <span class="lms-rotulo block">
        {{ $t('CAPTAIN.COPILOT.TRY_THESE_PROMPTS') }}
      </span>
      <div class="lms__lista">
        <button
          v-for="(prompt, i) in promptOptions"
          :key="prompt.label"
          type="button"
          class="lms-cmd group"
          :style="{ '--i': i }"
          @click="handleSuggestion(prompt)"
        >
          <span class="lms-cmd__ladrilho" aria-hidden="true">
            <i :class="prompt.icone" />
          </span>
          <span class="lms-cmd__txt">{{ t(prompt.label) }}</span>
          <Icon icon="i-ph-arrow-right" class="lms-cmd__seta shrink-0" />
        </button>
      </div>
      <!-- Transparência (08/10/2026): o administrador vê as perguntas em
           Lumos › Observabilidade (Lumos::PerguntasAoCopiloto). Quem pergunta
           precisa saber disso antes, não descobrir depois. -->
      <p class="lms__registro">
        <i class="i-ph-eye" aria-hidden="true" />
        {{ t('CAPTAIN.COPILOT.REGISTRO') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ÍRIS É A COR DA IA no produto inteiro. O selo grande saiu daqui: o topo do
   painel já é a faixa do Lumos com o selo; repetir era a mesma marca duas
   vezes, uma embaixo da outra. */
.lms__ola {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
  animation: lms-chega 0.45s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.lms__saudacao {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--iris-11));
}

.lms__titulo {
  margin: 0;
  font-family: var(--fonte-display, inherit);
  font-size: 21px;
  font-weight: 750;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: rgb(var(--slate-12));
}

.lms__dica {
  margin: 4px 0 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: rgb(var(--slate-11));
}

.lms__registro {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-3) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

.lms__registro > i {
  flex-shrink: 0;
  font-size: var(--ico-1);
}

.lms-rotulo {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--slate-10));
}

.lms__lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Os comandos: cartão com ladrilho do assunto. No cursor o cartão acende em
   íris, o ladrilho enche e a seta anda — é o único lugar do painel onde se
   age sem digitar, então vale convidar. Chegam em cascata. */
.lms-cmd {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px 10px 10px;
  border: 0;
  border-radius: 14px;
  background: rgb(var(--slate-2));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-4));
  color: rgb(var(--slate-12));
  text-align: start;
  cursor: pointer;
  animation: lms-chega 0.45s cubic-bezier(0.16, 1, 0.3, 1)
    calc(0.12s + var(--i, 0) * 60ms) backwards;
  transition:
    background-color 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.lms-cmd:hover {
  background: rgb(var(--iris-2));
  box-shadow:
    inset 0 0 0 1px rgb(var(--iris-7)),
    0 8px 18px -12px rgb(var(--iris-9) / 70%);
  transform: translateY(-1px);
}

.dark .lms-cmd:hover {
  background: rgb(var(--iris-3) / 0.35);
}

.lms-cmd:focus-visible {
  outline: 2px solid rgb(var(--iris-9));
  outline-offset: 2px;
}

.lms-cmd__ladrilho {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgb(var(--iris-3));
  font-size: 16px;
  color: rgb(var(--iris-11));
  transition:
    background-color 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    color 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.lms-cmd:hover .lms-cmd__ladrilho {
  background: linear-gradient(
    150deg,
    rgb(var(--iris-9)),
    rgb(var(--violet-10))
  );
  color: #fff;
}

.lms-cmd__txt {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 550;
  line-height: 1.35;
}

.lms-cmd__seta {
  color: rgb(var(--slate-9));
  transition:
    color 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.lms-cmd:hover .lms-cmd__seta {
  color: rgb(var(--iris-11));
  transform: translateX(3px);
}

@keyframes lms-chega {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .lms__ola,
  .lms-cmd,
  .lms-cmd:hover,
  .lms-cmd__seta {
    animation: none;
    transform: none;
    transition: none;
  }
}
</style>
